/**
 * Self-healing for failed translation-namespace loads.
 *
 * i18next caches a failed load and never retries it on its own, so a single
 * backend blip used to leave components rendering raw keys forever. This module
 * wires two reactions to i18next's `failedLoading` event:
 *
 *   1. DEGRADE NOW — seed the bundled critical fallback for any namespace the
 *      backend did not deliver, so the login surface stays readable;
 *   2. RECOVER LATER — bounded exponential retries via `reloadResources`, whose
 *      payload overwrites the seeded copy (the backend stays the source of
 *      truth). `bindI18nStore: 'added removed'` re-renders the tree when the
 *      late bundle finally lands.
 *
 * A failed read leaves i18next's state of that namespace at -1 (or 0), and a
 * namespace in that state is "nothing to do" for it EVEN with `reload: true`:
 * `reloadResources` resolved without asking the server anything (#4114). Every
 * retry therefore releases the state first (`releaseFailedNamespace`).
 *
 * Every failure is logged ONCE per language and audience and episode with
 * `logger.error` (it reaches the production console), a failure that asking
 * again cannot heal schedules no timers, and `TranslationLoadStatus` tells the
 * notice on screen what is going on.
 */

import type { i18n as I18nInstance } from 'i18next'

import { logger } from '@/shared/utils/logger'

import {
  describeBundleFailure,
  isTransientBundleFailure,
  resolveBundleAudience,
} from './bundle-backend'
import { applyCriticalFallbackForMissingNamespaces } from './fallback/critical-fallback-resources'
import {
  appTranslationLoadStatus,
  type TranslationLoadStatusWriter,
} from './translation-load-status'

/** Backoff schedule for retrying a namespace the backend failed to serve. */
const FAILED_LOAD_RETRY_DELAYS_MS = [3000, 10000, 30000] as const

/** i18next backend-connector state meaning "loaded from the backend". */
const NAMESPACE_LOAD_STATE_LOADED = 2

/** The per-namespace load states of i18next's backend connector (`{ 'es|auth': 2 }`), if it has them. */
const readLoadStates = (instance: I18nInstance): Record<string, unknown> | undefined => {
  const connector: unknown = instance.services?.backendConnector
  if (!connector || typeof connector !== 'object') return undefined
  const state: unknown = (connector as { state?: unknown }).state
  if (!state || typeof state !== 'object') return undefined
  return state as Record<string, unknown>
}

/**
 * Read i18next's per-namespace load state (`{ 'es|auth': 2 }`). Used to tell a
 * REAL backend load from our seeded fallback — `hasResourceBundle` cannot,
 * because the seed populates the very same store.
 */
const readNamespaceLoadState = (
  instance: I18nInstance,
  language: string,
  namespace: string,
): number | undefined => {
  const loadState = readLoadStates(instance)?.[`${language}|${namespace}`]
  return typeof loadState === 'number' ? loadState : undefined
}

/**
 * Forget the failed state of a namespace (-1 failed, 0 failed after retries) so the next
 * `reloadResources` really asks the backend again: i18next 25 skips a namespace whose state is
 * below one, `reload` or not. Reused by the live update of the catalogue (#4043).
 */
const releaseFailedNamespace = (
  instance: I18nInstance,
  language: string,
  namespace: string,
): void => {
  const loadState = readNamespaceLoadState(instance, language, namespace)
  if (loadState === undefined || loadState > 0) return
  const states = readLoadStates(instance)
  if (states) delete states[`${language}|${namespace}`]
}

/**
 * Install the failure handling on an i18next instance. Returns a disposer that
 * removes the listeners and cancels pending retries (used by tests; the app
 * keeps it installed for the lifetime of the page).
 */
export const installI18nFailureResilience = (
  instance: I18nInstance,
  status: TranslationLoadStatusWriter = appTranslationLoadStatus,
): (() => void) => {
  const retryAttemptsByNamespace = new Map<string, number>()
  const pendingRetryTimers = new Map<string, ReturnType<typeof setTimeout>>()
  /** Namespaces whose last read failed and has not recovered, by `language|namespace`. */
  const failedNamespaces = new Map<string, { language: string; namespace: string }>()
  /** Episodes already logged, by `language:audience`; cleared once that audience has nothing failed. */
  const loggedEpisodes = new Set<string>()
  let backgroundReloads = 0
  let disposed = false

  const hasFailedInAudience = (language: string, audience: string): boolean =>
    [...failedNamespaces.values()].some(
      (entry) => entry.language === language && resolveBundleAudience(entry.namespace) === audience,
    )

  const reloadReleased = async (language: string, namespaces: string[]): Promise<void> => {
    for (const namespace of namespaces) releaseFailedNamespace(instance, language, namespace)
    await instance.reloadResources(language, namespaces)
  }

  const handleFailedLoading = (language: string, namespace: string, error?: unknown): void => {
    const seededNamespaces = applyCriticalFallbackForMissingNamespaces(instance)
    if (seededNamespaces.length > 0) {
      logger.warn(
        `[i18n] Backend namespace load failed (${language}:${namespace}); serving bundled fallback for: ${seededNamespaces.join(', ')}`,
      )
    }

    failedNamespaces.set(`${language}|${namespace}`, { language, namespace })
    status.markFailed(language, namespace)

    const audience = resolveBundleAudience(namespace)
    const episode = `${language}:${audience}`
    if (!loggedEpisodes.has(episode)) {
      loggedEpisodes.add(episode)
      logger.error('[i18n] Translations could not be loaded', {
        language,
        audience,
        namespace,
        ...describeBundleFailure(error),
      })
    }

    // A broken contract or a missing manifest route does not change by asking again. A thrown value
    // that is not an Error (a bare string from a custom backend) carries no such proof: it is treated
    // as transient, so it keeps the bounded retries (and it was logged above like any other failure).
    if (error instanceof Error && !isTransientBundleFailure(error)) return

    const retryKey = `${language}:${namespace}`
    const attempt = retryAttemptsByNamespace.get(retryKey) ?? 0
    const delayMs = FAILED_LOAD_RETRY_DELAYS_MS[attempt]
    if (delayMs === undefined) return // retry budget exhausted for this namespace
    retryAttemptsByNamespace.set(retryKey, attempt + 1)

    const timer = setTimeout(() => {
      pendingRetryTimers.delete(retryKey)
      backgroundReloads += 1
      status.setRetrying(true)
      void reloadReleased(language, [namespace])
        .catch((reloadError: unknown) => {
          logger.error('[i18n] The background translation retry failed', {
            language,
            namespace,
            message: reloadError instanceof Error ? reloadError.message : String(reloadError),
          })
        })
        .finally(() => {
          if (disposed) return // the disposer already reset the counter and the status
          backgroundReloads -= 1
          if (backgroundReloads === 0) status.setRetrying(false)
        })
    }, delayMs)
    pendingRetryTimers.set(retryKey, timer)
  }

  /** Recovered = i18next says the backend delivered it (the bundled seed does not count). */
  const handleLoaded = (): void => {
    for (const [key, { language, namespace }] of [...failedNamespaces]) {
      if (readNamespaceLoadState(instance, language, namespace) !== NAMESPACE_LOAD_STATE_LOADED) {
        continue
      }
      failedNamespaces.delete(key)
      retryAttemptsByNamespace.delete(`${language}:${namespace}`)
      status.markLoaded(language, namespace)
      const audience = resolveBundleAudience(namespace)
      if (!hasFailedInAudience(language, audience)) loggedEpisodes.delete(`${language}:${audience}`)
    }
  }

  /** The manual retry: every missing namespace of the active language, final failures included. */
  const retryNow = async (): Promise<boolean> => {
    const language = instance.language
    const namespaces = [...failedNamespaces.values()]
      .filter((entry) => entry.language === language)
      .map((entry) => entry.namespace)
    for (const namespace of namespaces) {
      const retryKey = `${language}:${namespace}`
      clearTimeout(pendingRetryTimers.get(retryKey))
      pendingRetryTimers.delete(retryKey)
      retryAttemptsByNamespace.delete(retryKey)
      // A manual attempt opens a new episode: if it fails again, the NEW failure (another kind or
      // status) is logged, once per press and audience, instead of leaving the first one on the console.
      loggedEpisodes.delete(`${language}:${resolveBundleAudience(namespace)}`)
    }
    if (namespaces.length > 0) await reloadReleased(language, namespaces)
    return namespaces.every(
      (namespace) =>
        readNamespaceLoadState(instance, language, namespace) === NAMESPACE_LOAD_STATE_LOADED,
    )
  }

  instance.on('failedLoading', handleFailedLoading)
  instance.on('loaded', handleLoaded)
  const unbindStatus = status.bind(instance, retryNow)

  return (): void => {
    disposed = true
    instance.off('failedLoading', handleFailedLoading)
    instance.off('loaded', handleLoaded)
    unbindStatus()
    backgroundReloads = 0
    status.setRetrying(false)
    for (const timer of pendingRetryTimers.values()) clearTimeout(timer)
    pendingRetryTimers.clear()
    retryAttemptsByNamespace.clear()
    failedNamespaces.clear()
    loggedEpisodes.clear()
  }
}
